import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, ChevronLeft, UserPlus } from 'lucide-react';
import { chatApi } from '../../api/chat';
import { fetchUsers } from '../../api/users/user';
import useChat from '../../hooks/useChat';

const ChatPanel = ({ isOpen, onClose, onOpenExternally, onUnreadCountChange }) => {
    const [view, setView] = useState('list'); // 'list', 'chat', 'new'
    const [conversations, setConversations] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(false);

    // 신규 채팅 관련 상태
    const [users, setUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [groupName, setGroupName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const scrollRef = useRef(null);
    const projectId = localStorage.getItem('projectId');

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const { messages, setMessages, sendMessage, isConnected } = useChat();

    // 대화방 목록 로드
    const loadConversations = async () => {
        try {
            const res = await chatApi.getConversations(projectId);
            const data = res.data.results || res.data;
            setConversations(data);

            // 총 안읽은 개수 계산 및 부모 전달
            const total = data.reduce((acc, cur) => acc + (cur.unread_count || 0), 0);
            if (onUnreadCountChange) onUnreadCountChange(total);
        } catch (e) {
            console.error('Failed to load conversations', e);
        }
    };

    // 사용자 목록 로드
    const loadUsers = async () => {
        try {
            const res = await fetchUsers(projectId);
            setUsers(res.data.results || res.data || []);
        } catch (e) {
            console.error('Failed to load users', e);
        }
    };

    // 마운트 시 초기 로드
    useEffect(() => {
        loadConversations();
        loadUsers();
    }, []);

    // 패널이 열릴 때마다 리스트 갱신 (선택 사항)
    useEffect(() => {
        if (isOpen) {
            loadConversations();
        }
    }, [isOpen]);

    // WebSocket 메시지 기반으로 대화 리스트 갱신 (실시간 읽음/안읽음 연동)
    useEffect(() => {
        if (messages.length > 0) {
            const lastMsg = messages[messages.length - 1];

            setConversations(prev => {
                const next = prev.map(chat => {
                    if (String(chat.id) === String(lastMsg.conversation)) {
                        const isOther = String(lastMsg.sender) !== String(currentUser.id);
                        const isCurrent = selectedChat && String(selectedChat.id) === String(chat.id);

                        // 현재 보고 있는 채팅방이면 실시간 읽음 처리
                        if (isOther && isCurrent) {
                            chatApi.markAsRead(chat.id);
                        }

                        return {
                            ...chat,
                            last_message: { ...lastMsg, text: lastMsg.text || lastMsg.message }, // 필드명 호환
                            unread_count: (isOther && !isCurrent) ? (chat.unread_count + 1) : chat.unread_count
                        };
                    }
                    return chat;
                });

                // 총 안읽은 개수 다시 계산
                const total = next.reduce((acc, cur) => acc + (cur.unread_count || 0), 0);
                if (onUnreadCountChange) onUnreadCountChange(total);

                return next;
            });
        }
    }, [messages, selectedChat, currentUser.id]);

    // 검색 필터링
    const filteredUsers = users.filter(u => {
        const name = `${u.last_name || ''}${u.first_name || ''}` || u.username;
        return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            u.username.toLowerCase().includes(searchQuery.toLowerCase());
    });

    useEffect(() => {
        if (selectedChat) {
            setView('chat');
        } else if (view === 'chat') {
            setView('list');
        }
    }, [selectedChat]);

    // 외부(사용자 목록 등)에서 채팅 시작 요청 수신
    useEffect(() => {
        const handleOpenChat = async (e) => {
            const { userId } = e.detail;
            if (!userId) return;

            try {
                setLoading(true);
                const res = await chatApi.getOrCreate1on1(userId, projectId);
                const newChat = res.data;
                setSelectedChat(newChat);
                if (onOpenExternally) onOpenExternally();
            } catch (err) {
                console.error('Failed to open chat with user', err);
            } finally {
                setLoading(false);
            }
        };

        window.addEventListener('openChatWithUser', handleOpenChat);
        return () => window.removeEventListener('openChatWithUser', handleOpenChat);
    }, [onOpenExternally]);

    // 1:1 채팅 시작
    const handleStart1on1 = async (userId) => {
        try {
            setLoading(true);
            const res = await chatApi.getOrCreate1on1(userId, projectId);
            setSelectedChat(res.data);
        } catch (e) {
            console.error('Failed to start 1on1 chat', e);
        } finally {
            setLoading(false);
        }
    };

    // 그룹 채팅 생성
    const handleCreateGroup = async () => {
        if (selectedUserIds.length < 1) return;

        try {
            setLoading(true);
            const res = await chatApi.createConversation({
                project: projectId,
                participants: [...selectedUserIds, currentUser.id],
                is_group: true,
                name: groupName || `${currentUser.username}'s Group`
            });
            setSelectedChat(res.data);
            setSelectedUserIds([]);
            setGroupName('');
            loadConversations(); // 리스트 갱신
        } catch (e) {
            console.error('Failed to create group chat', e);
        } finally {
            setLoading(false);
        }
    };

    const toggleUserSelection = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    // 특정 대화방 선택 시 메시지 로드 및 읽음 처리
    useEffect(() => {
        if (selectedChat) {
            const loadMessages = async () => {
                setLoading(true);
                try {
                    const res = await chatApi.getMessages(selectedChat.id);
                    setMessages(res.data.results || res.data);

                    // 읽음 처리 API 호출
                    await chatApi.markAsRead(selectedChat.id);

                    // 로컬 상태에서도 안읽음 0으로 즉시 반영
                    setConversations(prev => {
                        const next = prev.map(chat =>
                            String(chat.id) === String(selectedChat.id)
                                ? { ...chat, unread_count: 0 }
                                : chat
                        );
                        const total = next.reduce((acc, cur) => acc + (cur.unread_count || 0), 0);
                        if (onUnreadCountChange) onUnreadCountChange(total);
                        return next;
                    });
                } catch (e) {
                    console.error('Failed to load messages', e);
                } finally {
                    setLoading(false);
                }
            };
            loadMessages();
        }
    }, [selectedChat, setMessages]);

    // 새 메시지 수신 시 스크롤 제어
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!inputText.trim() || !selectedChat) return;

        sendMessage(inputText, selectedChat.id);
        setInputText('');
    };

    const getOtherParticipant = (chat) => {
        if (chat.is_group) return { displayName: chat.name || 'Group Chat' };
        const other = chat.participants?.find(p => p.id !== currentUser.id) || {};
        return {
            ...other,
            displayName: other.name || other.username || 'Unknown'
        };
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    className="fixed top-0 right-0 h-full w-full sm:w-[400px] bg-[#1e1e2f] text-white z-[60] shadow-2xl flex flex-col no-print"
                >
                    {/* Header */}
                    <div className="p-4 border-b border-white/10 flex items-center justify-between bg-[#2a2a40]">
                        <div className="flex items-center gap-3">
                            {view !== 'list' && (
                                <button
                                    onClick={() => {
                                        if (view === 'chat') setSelectedChat(null);
                                        setView('list');
                                        setSelectedUserIds([]);
                                        setGroupName('');
                                    }}
                                    className="p-1 hover:bg-white/10 rounded-full"
                                >
                                    <ChevronLeft size={24} />
                                </button>
                            )}
                            <div className="flex items-center gap-3">
                                {view === 'chat' && selectedChat && (
                                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-xs overflow-hidden shrink-0">
                                        {getOtherParticipant(selectedChat).profile_picture ? (
                                            <img src={getOtherParticipant(selectedChat).profile_picture} alt="avatar" className="w-full h-full object-cover" />
                                        ) : (
                                            (getOtherParticipant(selectedChat).displayName || '?').slice(0, 1).toUpperCase()
                                        )}
                                    </div>
                                )}
                                <h2 className="text-lg font-bold">
                                    {view === 'chat' && selectedChat ? getOtherParticipant(selectedChat).displayName :
                                        view === 'new' ? '새 채팅' : '대화 내역'}
                                </h2>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {view === 'list' && (
                                <button
                                    onClick={() => setView('new')}
                                    className="p-2 hover:bg-white/10 rounded-full text-blue-400"
                                    title="New Chat"
                                >
                                    <UserPlus size={20} />
                                </button>
                            )}
                            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
                                <X size={24} />
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-hidden flex flex-col">
                        {view === 'chat' && selectedChat ? (
                            /* Chat Window */
                            <>
                                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
                                    {loading ? (
                                        <div className="flex justify-center py-10">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                        </div>
                                    ) : (
                                        messages.filter(m => String(m.conversation) === String(selectedChat.id)).map((msg) => (
                                            <div
                                                key={msg.id || Math.random()}
                                                className={`flex ${msg.sender === currentUser.id ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div className={`max-w-[80%] p-3 rounded-2xl ${msg.sender === currentUser.id
                                                    ? 'bg-blue-600 rounded-tr-none'
                                                    : 'bg-white/10 rounded-tl-none'
                                                    }`}>
                                                    {selectedChat.is_group && msg.sender !== currentUser.id && (
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center text-[10px] text-blue-400 font-bold overflow-hidden shrink-0">
                                                                {msg.sender_profile_picture ? (
                                                                    <img src={msg.sender_profile_picture} alt="avatar" className="w-full h-full object-cover" />
                                                                ) : (
                                                                    (msg.sender_name || '?').slice(0, 1).toUpperCase()
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-blue-300">{msg.sender_name}</div>
                                                        </div>
                                                    )}
                                                    <p className="text-sm break-words whitespace-pre-wrap">{msg.text}</p>
                                                    <div className="text-[10px] opacity-50 mt-1 flex items-center justify-end gap-1">
                                                        {msg.sender === currentUser.id && (
                                                            <span className="text-blue-300 font-bold mr-1">
                                                                {msg.is_read ? '' : '1'}
                                                            </span>
                                                        )}
                                                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                {/* Input Area */}
                                <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10 bg-[#2a2a40]">
                                    <div className="flex items-center gap-2 bg-white/5 rounded-full px-4 py-2 border border-white/10 focus-within:border-blue-500 transition-colors">
                                        <input
                                            type="text"
                                            value={inputText}
                                            onChange={(e) => setInputText(e.target.value)}
                                            placeholder="Type a message..."
                                            className="flex-1 bg-transparent border-none focus:outline-none text-sm py-1"
                                        />
                                        <button
                                            type="submit"
                                            disabled={!inputText.trim() || !isConnected}
                                            className="text-blue-500 hover:text-blue-400 disabled:opacity-50 transition-colors"
                                        >
                                            <Send size={20} />
                                        </button>
                                    </div>
                                </form>
                            </>
                        ) : view === 'new' ? (
                            /* New Chat / User Selection View */
                            <div className="flex-1 flex flex-col overflow-hidden">
                                <div className="p-4 space-y-3 bg-[#2a2a40]/50 border-b border-white/5">
                                    <input
                                        type="text"
                                        placeholder="사용자 검색..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                                    />
                                    {selectedUserIds.length > 0 && (
                                        <div className="flex flex-col gap-2">
                                            <input
                                                type="text"
                                                placeholder="그룹 이름"
                                                value={groupName}
                                                onChange={(e) => setGroupName(e.target.value)}
                                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                                            />
                                            <button
                                                onClick={handleCreateGroup}
                                                className="w-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
                                            >
                                                그룹 생성 ({selectedUserIds.length + 1} 명)
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 overflow-y-auto no-scrollbar">
                                    {filteredUsers.filter(u => u.id !== currentUser.id).map(user => (
                                        <div key={user.id} className="p-3 flex items-center justify-between hover:bg-white/5 border-b border-white/5 group">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold overflow-hidden shrink-0">
                                                    {user.profile_picture ? (
                                                        <img src={user.profile_picture} alt={user.username} className="w-full h-full object-cover" />
                                                    ) : (
                                                        (user.username || '?').slice(0, 1).toUpperCase()
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold">
                                                        {user.last_name}{user.first_name}
                                                        {user.company && <span className="ml-2 text-[10px] text-blue-300 font-normal">({user.company})</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleStart1on1(user.id)}
                                                    className="px-3 py-1 bg-white/5 hover:bg-white/10 text-[11px] rounded transition-colors"
                                                >
                                                    1:1 Chat
                                                </button>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUserIds.includes(user.id)}
                                                    onChange={() => toggleUserSelection(user.id)}
                                                    className="w-4 h-4 rounded border-white/20 bg-transparent text-blue-600 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            /* Conversation List */
                            <div className="flex-1 overflow-y-auto no-scrollbar">
                                {conversations.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center p-8 text-center text-gray-500">
                                        <MessageSquare size={48} className="mb-4 opacity-20" />
                                        <p>No conversations yet.</p>
                                        <p className="text-sm mt-1">Click the + icon above to start a chat.</p>
                                    </div>
                                ) : (
                                    conversations.map((chat) => (
                                        <button
                                            key={chat.id}
                                            onClick={() => setSelectedChat(chat)}
                                            className="w-full p-4 flex items-center gap-4 hover:bg-white/5 border-b border-white/5 transition-colors text-left"
                                        >
                                            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-xl relative shrink-0 overflow-hidden">
                                                {getOtherParticipant(chat).profile_picture ? (
                                                    <img src={getOtherParticipant(chat).profile_picture} alt="avatar" className="w-full h-full object-cover" />
                                                ) : (
                                                    (getOtherParticipant(chat).displayName || '?').slice(0, 1).toUpperCase()
                                                )}
                                                {chat.unread_count > 0 && (
                                                    <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center border-2 border-[#1e1e2f] z-10">
                                                        {chat.unread_count}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start mb-1">
                                                    <h3 className="font-semibold truncate">
                                                        {getOtherParticipant(chat).displayName}
                                                        {!chat.is_group && getOtherParticipant(chat).company && (
                                                            <span className="ml-2 text-[10px] text-blue-300 font-normal">
                                                                ({getOtherParticipant(chat).company})
                                                            </span>
                                                        )}
                                                    </h3>
                                                    <span className="text-[10px] text-gray-500 whitespace-nowrap ml-2">
                                                        {chat.last_message ? new Date(chat.last_message.created_at).toLocaleDateString() : ''}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-400 truncate">
                                                    {chat.last_message ? chat.last_message.text : 'No messages yet'}
                                                </p>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ChatPanel;
